import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaBuilding, FaHistory, FaSearch, FaTimes } from "react-icons/fa";
import { fetchWithFallback } from "../OrgUsageAnalytics/api";
import { Avatar } from "../OrgUsageAnalytics/components/Avatar";
import { useOrgCache, normalizeOrg } from "./useOrgCache";

const RECENTS_KEY = "oo-recent-orgs";
const RECENTS_MAX = 6;
const RESULT_LIMIT = 12;

export function readRecents() {
  try {
    const raw = window.localStorage.getItem(RECENTS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((o) => o && o.id) : [];
  } catch {
    return [];
  }
}

export function pushRecentOrg(org) {
  if (!org || !org.id) return;
  try {
    const existing = readRecents().filter((o) => o.id !== org.id);
    const next = [{ id: org.id, name: org.name || org.id }, ...existing].slice(
      0,
      RECENTS_MAX,
    );
    window.localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch {
    // ignore quota / private-mode errors
  }
}

/**
 * Fast org switcher.
 *
 * - On mount it loads a lightweight (id + name) org list from Firestore for
 *   instant client-side filtering — the same approach LoanStageAnalytics uses.
 * - As the user types it ALSO runs a debounced server-side search against
 *   `/admin/organizations/usage-limits?search=` so orgs beyond the first
 *   `ORG_CACHE_LIMIT` (there are ~22k tenants) are still reachable.
 * - Recently-viewed orgs are persisted in localStorage for one-click return.
 */
export default function OrgPicker({ env, selectedOrg, onSelect, onClear }) {
  const [open, setOpen] = useState(false);
  const [queryText, setQueryText] = useState("");
  const [debounced, setDebounced] = useState("");
  const { orgs: cache } = useOrgCache(env);
  const [serverResults, setServerResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [recents, setRecents] = useState(readRecents);
  const wrapRef = useRef(null);
  const searchSeq = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(queryText.trim()), 250);
    return () => clearTimeout(t);
  }, [queryText]);

  // Server-side search to cover orgs beyond the cached page.
  useEffect(() => {
    if (debounced.length < 2) {
      setServerResults([]);
      setSearching(false);
      return undefined;
    }
    const seq = ++searchSeq.current;
    setSearching(true);
    let cancelled = false;
    fetchWithFallback(
      "/organizations/usage-limits",
      { search: debounced },
      { env, timeoutMs: 30000 },
    )
      .then((json) => {
        if (cancelled || seq !== searchSeq.current) return;
        const orgs = Array.isArray(json?.orgs)
          ? json.orgs.map((o) => normalizeOrg(o.id, o))
          : [];
        setServerResults(orgs);
      })
      .catch(() => {
        if (!cancelled && seq === searchSeq.current) setServerResults([]);
      })
      .finally(() => {
        if (!cancelled && seq === searchSeq.current) setSearching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debounced, env]);

  // Close on outside click.
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const results = useMemo(() => {
    const q = debounced.toLowerCase();
    const seen = new Set();
    const merged = [];
    const add = (org) => {
      if (!org || !org.id || seen.has(org.id)) return;
      seen.add(org.id);
      merged.push(org);
    };
    if (q) {
      cache
        .filter(
          (o) => o.name.toLowerCase().includes(q) || o.id.toLowerCase().includes(q),
        )
        .forEach(add);
      serverResults.forEach(add);
    } else {
      cache.forEach(add);
    }
    return merged.slice(0, RESULT_LIMIT);
  }, [cache, serverResults, debounced]);

  const handleSelect = useCallback(
    (org) => {
      pushRecentOrg(org);
      setRecents(readRecents());
      setOpen(false);
      setQueryText("");
      setDebounced("");
      onSelect(org);
    },
    [onSelect],
  );

  const showRecents = !debounced && recents.length > 0;

  return (
    <div className="oo-picker" ref={wrapRef}>
      <div className="oo-picker-control" onClick={() => setOpen(true)}>
        <FaSearch className="oo-picker-icon" />
        <input
          className="oo-picker-input"
          value={queryText}
          placeholder={
            selectedOrg ? "Switch organization…" : "Search organizations by name or ID…"
          }
          onChange={(e) => {
            setQueryText(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
        />
        {selectedOrg ? (
          <span className="oo-picker-current" title={selectedOrg.id}>
            <Avatar name={selectedOrg.name} seed={selectedOrg.id} size="sm" />
            <span className="oo-picker-current-name">{selectedOrg.name}</span>
            {onClear ? (
              <button
                type="button"
                className="oo-picker-current-clear"
                onClick={(e) => {
                  e.stopPropagation();
                  setQueryText("");
                  setDebounced("");
                  setOpen(false);
                  onClear();
                }}
                aria-label="Clear selected organization"
                title="Back to all organizations"
              >
                <FaTimes />
              </button>
            ) : null}
          </span>
        ) : null}
        {queryText ? (
          <button
            type="button"
            className="oo-picker-clear"
            onClick={(e) => {
              e.stopPropagation();
              setQueryText("");
              setDebounced("");
            }}
            aria-label="Clear search"
          >
            <FaTimes />
          </button>
        ) : null}
      </div>

      {open ? (
        <div className="oo-picker-menu">
          {showRecents ? (
            <div className="oo-picker-section">
              <div className="oo-picker-section-label">
                <FaHistory /> Recently viewed
              </div>
              {recents.map((org) => (
                <button
                  key={`recent-${org.id}`}
                  type="button"
                  className="oo-picker-row"
                  onClick={() => handleSelect(org)}
                >
                  <Avatar name={org.name} seed={org.id} size="sm" />
                  <span className="oo-picker-row-text">
                    <span className="oo-picker-row-name">{org.name}</span>
                    <span className="oo-picker-row-id">{org.id}</span>
                  </span>
                </button>
              ))}
            </div>
          ) : null}

          <div className="oo-picker-section">
            <div className="oo-picker-section-label">
              <FaBuilding /> Organizations
              {searching ? <span className="oo-picker-spinner" /> : null}
            </div>
            {results.length ? (
              results.map((org) => (
                <button
                  key={org.id}
                  type="button"
                  className="oo-picker-row"
                  onClick={() => handleSelect(org)}
                >
                  <Avatar name={org.name} seed={org.id} size="sm" />
                  <span className="oo-picker-row-text">
                    <span className="oo-picker-row-name">{org.name}</span>
                    <span className="oo-picker-row-id">{org.id}</span>
                  </span>
                </button>
              ))
            ) : (
              <div className="oo-picker-empty">
                {debounced
                  ? searching
                    ? "Searching…"
                    : "No organizations match. Try a different name or paste an org ID."
                  : cache.length
                    ? "Start typing to search every organization."
                    : "Loading organizations…"}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
